// oxlint-disable typescript/unbound-method -- Logger is a plain object interface, no this binding needed
import { decodeTime, isDev, isProd, toLogFmt } from '@w/utils'
import colors from 'picocolors'
import type { BoundTelExporter, TelExporter } from './tel-storage'
import type { TelServerErrorData } from './tel-error-data'
import type { MetricPoint, QuantileSketchData } from './tel-metrics-types'
import { mergeDDSketchStates, percentilesFromDDSketchState } from './ddsketch'
import type { Attrs } from './attrs'
import type { LogLevel, SpanData } from './tel-types'

function formatTimeFromUlid(ulid: string): string {
  try {
    return new Date(decodeTime(ulid)).toISOString().slice(11, 19)
  } catch {
    return ''
  }
}

function getLevelPrefix(level: LogLevel): string {
  // oxlint-disable-next-line typescript/switch-exhaustiveness-check -- default handles other levels
  switch (level) {
    case 'info':
      return 'ℹ'
    case 'warn':
      return '⚠'
    case 'error':
      return '✖'
    default:
      return level.toUpperCase()
  }
}

export interface Logger {
  info(...args: unknown[]): void
  error(...args: unknown[]): void
  warn(...args: unknown[]): void
  dir(obj: unknown, options?: { depth: number | null }): void
}

export interface ConsoleMetricsOptions {
  delayMs?: number
}

export interface ConsoleSpanOptions {
  delayMs?: number
}

export interface ConsoleExporterOptions {
  labels?: string[] | 'all'
  targets?: NonNullable<SpanData['target']>[] | 'all'
  minimal?: boolean
  cloudLogs?: boolean
  colored?: boolean
  metrics?: false | ConsoleMetricsOptions
  spans?: false | ConsoleSpanOptions
  spanEvents?: boolean
  logs?: boolean
}

export type ConsoleExporter = TelExporter

export function consoleExport(
  opts: ConsoleExporterOptions = {},
): ConsoleExporter {
  return {
    bind: () => _bindConsoleExporter(opts, console),
  }
}

export function _bindConsoleExporter(
  {
    labels = [],
    targets = ['rpc-handler', 'server-handler'],
    minimal = false,
    cloudLogs = isProd,
    colored = true,
    metrics = {},
    spans = isProd && !isDev ? {} : false,
    spanEvents = true,
    logs = true,
  }: ConsoleExporterOptions,
  logger: Logger,
): BoundTelExporter {
  const spansDelayMs = spans === false ? null : (spans.delayMs ?? 3_000)
  const metricsDelayMs = metrics === false ? null : (metrics.delayMs ?? 60_000)
  let spansTimer: ReturnType<typeof setTimeout> | null = null
  let metricsTimer: ReturnType<typeof setTimeout> | null = null
  const metricSummaries = new Map<string, MetricSummary>()
  const metricTotals = new Map<string, number>()
  const spanSummaries = new Map<string, SpanSummary>()

  function scheduleSpansFlush() {
    if (spansDelayMs === null) return
    if (spansTimer) return
    spansTimer = setTimeout(() => {
      spansTimer = null
      void flushSpans()
    }, spansDelayMs)
  }

  function flushSpans(): Promise<void> {
    if (spansTimer) {
      clearTimeout(spansTimer)
      spansTimer = null
    }
    if (spanSummaries.size === 0) {
      return Promise.resolve()
    }

    const ts = _formatEventPrefix(_formatTime(Date.now()))
    for (const summary of spanSummaries.values()) {
      logger.info(`${ts}${formatSpanSummary(summary, colored)}`)
    }
    spanSummaries.clear()
    return Promise.resolve()
  }

  async function flush(): Promise<void> {
    await Promise.all([flushSpans(), flushMetrics()])
  }

  function flushMetrics(): Promise<void> {
    if (metricsTimer) {
      clearTimeout(metricsTimer)
      metricsTimer = null
    }
    if (metricSummaries.size === 0) return Promise.resolve()

    const ts = _formatEventPrefix(_formatTime(Date.now()))
    for (const [key, summary] of metricSummaries) {
      printMetricSummary(key, summary, ts)
    }
    metricSummaries.clear()
    return Promise.resolve()
  }

  function scheduleMetricsFlush(): void {
    if (metricsDelayMs === null || metricsTimer) return
    if (metricsDelayMs === 0) {
      void flushMetrics()
      return
    }
    metricsTimer = setTimeout(() => {
      metricsTimer = null
      void flushMetrics()
    }, metricsDelayMs)
  }

  function printMetricSummary(key: string, summary: MetricSummary, ts: string): void {
    if (summary.family === 'counter') {
      const previous = metricTotals.get(key)
      if (summary.gauge) {
        if (previous === summary.value) return
        summary.total = summary.value
      } else {
        if (summary.value === 0) return
        summary.total = (previous ?? 0) + summary.value
      }
      metricTotals.set(key, summary.total)
    }
    logger.info(`${ts}${formatMetricSummary(summary, minimal)}`)
  }

  function flushMetricKey(metric: MetricPoint): void {
    const key = metric.streamMeta.id
    const summary = metricSummaries.get(key)
    if (!summary) return
    const ts = _formatEventPrefix(_formatTime(Date.now()))
    printMetricSummary(key, summary, ts)
    metricSummaries.delete(key)
  }

  function addMetric(metric: MetricPoint): void {
    const normalized = normalizeMetric(metric)
    if (!normalized) return

    const key = metric.streamMeta.id
    const existing = metricSummaries.get(key)
    if (normalized.family === 'counter') {
      const summary: CounterSummary =
        existing && existing.family === 'counter'
          ? existing
          : {
              family: 'counter',
              name: normalized.name,
              attributes: normalized.attributes ? { ...normalized.attributes } : undefined,
              total: 0,
              value: 0,
              gauge: normalized.gauge,
            }

      summary.value = normalized.gauge ? normalized.value : (summary.value ?? 0) + normalized.value
      if (normalized.periodStartMs !== undefined) {
        summary.firstSeenAt =
          summary.firstSeenAt === undefined
            ? normalized.periodStartMs
            : Math.min(summary.firstSeenAt, normalized.periodStartMs)
      }
      if (normalized.periodEndMs !== undefined) {
        summary.lastSeenAt =
          summary.lastSeenAt === undefined
            ? normalized.periodEndMs
            : Math.max(summary.lastSeenAt, normalized.periodEndMs)
      }
      metricSummaries.set(key, summary)
      return
    }

    if (normalized.count === 0) return

    const summary: HistogramSummary =
      existing && existing.family === 'histogram'
        ? existing
        : {
            family: 'histogram',
            name: normalized.name,
            attributes: normalized.attributes ? { ...normalized.attributes } : undefined,
            count: 0,
            sum: 0,
            min: Number.POSITIVE_INFINITY,
            max: Number.NEGATIVE_INFINITY,
            quantileSketch: undefined,
          }

    summary.count += normalized.count
    summary.sum += normalized.sum
    summary.min = Math.min(summary.min, normalized.min)
    summary.max = Math.max(summary.max, normalized.max)
    summary.quantileSketch = mergeDDSketchStates([...(summary.quantileSketch ? [summary.quantileSketch] : []), normalized.quantileSketch])
    metricSummaries.set(key, summary)
  }

  function addSpan(span: SpanData): void {
    const normalized = normalizeSpan(span, targets, labels)
    if (!normalized) return

    const key = _spanKey(normalized)
    const existing = spanSummaries.get(key)
    if (existing) {
      existing.count++
    } else {
      spanSummaries.set(key, { ...normalized, count: 1 })
    }
    scheduleSpansFlush()
  }

  return {
    spans: {
      onCreate: (span) => {
        if (!spanEvents) return
        if (spans !== false) return
        const ts = formatTimeFromUlid(span.id)
        const prefix = _formatEventPrefix(ts)
        if (isRequestSpan(span)) {
          const name = colored ? colors.bold(colors.blue(span.name)) : span.name
          _logEvent(
            logger.info,
            `${prefix}↓ ${getColoredType(span, colored)} ${name}${minimal || span.input === undefined ? '' : ' input:'}`,
            span.input,
            { minimal, cloudLogs, logger },
          )
        } else if (
          span.target &&
          (targets === 'all' || targets.includes(span.target))
        ) {
          const targetPrefix = colorizePrefix(span.target, span.type, colored)
          const name = colored ? colors.bold(span.name) : span.name
          _logEvent(
            logger.info,
            `${prefix}↓ ${targetPrefix} ${name}${minimal || span.input === undefined ? '' : ' input:'}`,
            span.input,
            { minimal, cloudLogs, logger },
          )
        } else if (
          span.label &&
          (labels === 'all' || labels.includes(span.label))
        ) {
          const label = colorizePrefix(span.label, span.type, colored)
          _logEvent(
            logger.info,
            `${prefix}↓ ${label} ${span.name}${minimal || span.input === undefined ? '' : ' input:'}`,
            span.input,
            { minimal, cloudLogs, logger },
          )
        }
      },
      onEnd: (span) => {
        if (!spanEvents) return
        if (spans !== false) {
          addSpan(span)
          return
        }
        const ts = formatTimeFromUlid(span.id)
        const prefix = _formatEventPrefix(ts)
        if (isRequestSpan(span)) {
          const name = colored ? colors.bold(colors.blue(span.name)) : span.name
          const statusLog = span.error
            ? (span.error as TelServerErrorData).error.status < 500
              ? colored
                ? colors.yellow('ERROR')
                : 'ERROR'
              : colored
                ? colors.red('ERROR')
                : 'ERROR'
            : colored
              ? colors.green('OK')
              : 'OK'
          _logEvent(
            logger.info,
            `${prefix}↑ ${getColoredType(span, colored)} ${name}${minimal || (span.output === undefined && !span.error) ? '' : ` ${statusLog} output:`}`,
            span.output ?? span.error,
            { minimal, cloudLogs, logger },
          )
        } else if (
          span.target &&
          (targets === 'all' || targets.includes(span.target))
        ) {
          const targetPrefix = colorizePrefix(span.target, span.type, colored)
          const name = colored ? colors.bold(span.name) : span.name
          _logEvent(
            logger.info,
            `${prefix}↑ ${targetPrefix} ${name}${minimal || span.output === undefined ? '' : ' output:'}`,
            span.output,
            { minimal, cloudLogs, logger },
          )
        } else if (
          span.label &&
          (labels === 'all' || labels.includes(span.label))
        ) {
          const label = colorizePrefix(span.label, span.type, colored)
          _logEvent(
            logger.info,
            `${prefix}↑ ${label} ${span.name}${minimal || span.output === undefined ? '' : ' output:'}`,
            span.output,
            { minimal, cloudLogs, logger },
          )
        }
      },
    },
    logs: {
      onCreate: (log) => {
        if (!logs) return
        const ts = formatTimeFromUlid(log.id)
        const prefix = _formatEventPrefix(ts)
        const levelPrefix = getLevelPrefix(log.level)
        const attrs = log.attributes ? ` ${toLogFmt(log.attributes)}` : ''
        const msg = `${prefix}${levelPrefix} ${_formatInlineData(log.body)}${attrs}`
        const logFn =
          log.level === 'warn'
            ? logger.warn
            : log.level === 'error' || log.level === 'fatal'
              ? logger.error
              : logger.info
        logFn(msg)
      },
    },
    errors: {
      onCreate: ({ data }) => {
        logErrorOrWarning('🛑', 'error', data, colored, logger, cloudLogs)
      },
    },
    warnings: {
      onCreate: ({ data }) => {
        logErrorOrWarning('⚠️', 'warning', data, colored, logger, cloudLogs)
      },
    },
    metrics: {
      onCreateDelta: (metric) => {
        if (metrics === false) return
        addMetric(metric)
        if (metricsDelayMs === 0) flushMetricKey(metric)
        else scheduleMetricsFlush()
      },
      onCreateGauge: (metric) => {
        if (metrics === false) return
        addMetric(metric)
        if (metricsDelayMs === 0) flushMetricKey(metric)
        else scheduleMetricsFlush()
      },
    },
    flush,
    shutdown: async () => {
      await flush()
      metricTotals.clear()
    },
  }
}

interface SpanSummary {
  scope: string
  type?: SpanData['type']
  name: string
  status: string
  count: number
}

type MetricSummary = CounterSummary | HistogramSummary

interface CounterSummary {
  family: 'counter'
  name: string
  attributes?: Attrs
  total: number
  value: number
  gauge: boolean
  firstSeenAt?: number
  lastSeenAt?: number
}

interface HistogramSummary {
  family: 'histogram'
  name: string
  attributes?: Attrs
  count: number
  sum: number
  min: number
  max: number
  quantileSketch?: QuantileSketchData
}

function normalizeSpan(
  span: SpanData,
  targets: NonNullable<ConsoleExporterOptions['targets']>,
  labels: NonNullable<ConsoleExporterOptions['labels']>,
): Omit<SpanSummary, 'count'> | null {
  const scope = isRequestSpan(span)
    ? span.target
    : span.target && (targets === 'all' || targets.includes(span.target))
      ? span.target
      : span.label && (labels === 'all' || labels.includes(span.label))
        ? span.label
        : null
  if (!scope) return null
  return {
    scope,
    type: span.type,
    name: span.name,
    status: span.status ?? (span.error ? 'error' : 'ok'),
  }
}

function isRequestSpan(span: SpanData): boolean {
  return span.id === span.requestId
}

function formatSpanSummary(summary: SpanSummary, colored: boolean): string {
  const prefix = colorizePrefix(summary.scope, summary.type, colored)
  const type = getColoredType(summary, colored)
  const name = colored ? colors.bold(summary.name) : summary.name
  const status = colorizeStatus(summary.status, colored)
  return `SPAN ${prefix} ${type} ${name} ${status} count=${summary.count}`
}

function colorizeStatus(status: string, colored: boolean): string {
  if (!colored) return status
  if (status === 'ok') return colors.green(status)
  if (status === 'warning') return colors.yellow(status)
  if (status === 'error') return colors.red(status)
  return status
}

function _spanKey(summary: Omit<SpanSummary, 'count'>): string {
  return `${summary.scope}|${summary.type ?? ''}|${summary.name}|${summary.status}`
}

function normalizeMetric(metric: MetricPoint):
  | {
      family: 'counter'
      name: string
      attributes?: Attrs
      value: number
      gauge: boolean
      periodStartMs?: number
      periodEndMs?: number
    }
  | {
      family: 'histogram'
      name: string
      attributes?: Attrs
      count: number
      sum: number
      min: number
      max: number
      quantileSketch: QuantileSketchData
    }
  | null {
  const meta = metric.streamMeta
  if (meta.kind === 'gauge' || meta.kind === 'counter' || meta.kind === 'upDownCounter') {
      const value = (metric as { value: number }).value
      return {
        family: 'counter',
        name: meta.name,
        attributes: meta.attrs,
          value,
        gauge: meta.kind === 'gauge',
        periodStartMs: 'periodStartAt' in metric ? metric.periodStartAt : metric.collectedAt,
        periodEndMs: metric.collectedAt,
      }
  }
  if (meta.kind === 'histogram') {
      const point = metric as Extract<MetricPoint, { histogram: unknown }>
      const stats = point.histogram.stats
      return {
        family: 'histogram',
        name: meta.name,
        attributes: meta.attrs,
        count: stats.count,
        sum: stats.sum,
        min: stats.minMax?.min ?? 0,
        max: stats.minMax?.max ?? 0,
        quantileSketch: point.quantileSketch,
      }
  }
  const point = metric as Extract<MetricPoint, { exponentialHistogram: unknown }>
  const stats = point.exponentialHistogram.stats
  return { family: 'histogram', name: meta.name, attributes: meta.attrs, count: stats.count, sum: stats.sum, min: stats.minMax?.min ?? 0, max: stats.minMax?.max ?? 0, quantileSketch: point.quantileSketch }
}

function formatMetricSummary(summary: MetricSummary, minimal: boolean): string {
  const attrs = _formatAttrs(summary.attributes, minimal)
  if (summary.family === 'counter') {
    const durationMs =
      summary.firstSeenAt !== undefined && summary.lastSeenAt !== undefined
        ? summary.lastSeenAt - summary.firstSeenAt
        : 0
    const rate = summary.gauge ? '' : _formatRate(summary.value, durationMs)
    return `COUNTER ${summary.name} total=${_formatNumber(summary.total)} value=${_formatNumber(summary.value ?? 0)}${rate ? ` ${rate}` : ''}${attrs}`
  }

  const avg = summary.count === 0 ? 0 : summary.sum / summary.count
  const percentiles =
    summary.count > 0 && summary.quantileSketch
      ? percentilesFromDDSketchState(summary.quantileSketch)
      : undefined

  const percentileText = percentiles
    ? ` p50=${_formatNumber(percentiles.p50)} p95=${_formatNumber(percentiles.p95)} p99=${_formatNumber(percentiles.p99)}`
    : ''

  return `HISTOGRAM ${summary.name} count=${summary.count} avg=${_formatNumber(avg)} min=${_formatNumber(summary.min)} max=${_formatNumber(summary.max)}${percentileText}${attrs}`
}

function _formatRate(total: number, durationMs: number): string {
  if (durationMs <= 0) return ''
  const perSec = total / (durationMs / 1000)
  if (Math.abs(perSec) >= 1000)
    return `${(perSec / 1000).toFixed(1).replace(/\.0$/, '')}k/sec`
  if (Math.abs(perSec) >= 1) return `${perSec.toFixed(1).replace(/\.0$/, '')}/sec`
  const perMin = perSec * 60
  if (Math.abs(perMin) >= 1) return `${perMin.toFixed(1).replace(/\.0$/, '')}/min`
  return `${(perSec * 3600).toFixed(1).replace(/\.0$/, '')}/hr`
}

function _stableAttrs(attributes?: Attrs): string {
  if (!attributes) return ''
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(attributes).toSorted(([a], [b]) => a.localeCompare(b)),
    ),
  )
}

function _formatAttrs(attributes: Attrs | undefined, minimal: boolean): string {
  if (minimal || !attributes || Object.keys(attributes).length === 0) return ''
  return ` attrs=${_stableAttrs(attributes)}`
}

function _formatNumber(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(2).replace(/\.00$/, '')
}

function colorizePrefix(
  prefix: string,
  spanType: 'mutation' | 'query' | 'subscription' | undefined,
  colored: boolean,
): string {
  if (!colored) return prefix
  const color = spanType === 'query' ? colors.green : colors.blue
  return colors.bold(color(prefix))
}

function logErrorOrWarning(
  emoji: string,
  type: 'error' | 'warning',
  errorData: { error: { type: string; message: string }; spanId?: string },
  colored: boolean,
  logger: Logger,
  cloudLogs: boolean,
): void {
  const color = type === 'error' ? colors.red : colors.yellow
  const logFn = type === 'error' ? logger.error : logger.warn
  const ts = _formatTime(Date.now())
  const tsPrefix = ts ? `[${ts}] ` : ''
  const message = colored
    ? colors.bold(
        color(`${errorData.error.type}: ${errorData.error.message} !!!`),
      )
    : `${errorData.error.type}: ${errorData.error.message} !!!`
  if (cloudLogs) {
    logFn(
      `${tsPrefix}${emoji} ${message} error: ${_formatInlineData(errorData.error)}`,
    )
    return
  }
  logFn(`${tsPrefix}${emoji}`, message)
  logData(errorData.error, false, logger)
}

function logData(data: unknown, minimal: boolean, logger: Logger): void {
  if (minimal) return
  if (typeof data === 'object' && data) {
    logger.dir(data, { depth: null })
  } else {
    logger.info(data)
  }
}

function _logEvent(
  logFn: (...args: unknown[]) => void,
  message: string,
  data: unknown,
  opts: { minimal: boolean; cloudLogs: boolean; logger: Logger },
): void {
  if (opts.minimal || data === undefined) {
    logFn(message)
    return
  }
  if (opts.cloudLogs) {
    logFn(`${message} ${_formatInlineData(data)}`)
    return
  }
  logFn(message)
  logData(data, opts.minimal, opts.logger)
}

function _formatTime(epochMs: number): string {
  return new Date(epochMs).toISOString().slice(11, 19)
}

function _formatEventPrefix(ts: string): string {
  const tsPrefix = ts ? `[${ts}] ` : ''
  return tsPrefix
}

function _formatInlineData(data: unknown): string {
  if (typeof data === 'string') return _normalizeInlineText(data)
  if (typeof data !== 'object' || data === null) {
    return _normalizeInlineText(String(data))
  }
  const seen = new WeakSet<object>()
  try {
    const json = JSON.stringify(data, (_key, value) => {
      if (typeof value === 'bigint') return `${value}n`
      if (typeof value === 'function') {
        // oxlint-disable-next-line typescript/no-unsafe-member-access, typescript/prefer-nullish-coalescing -- JSON.stringify replacer receives unknown value per spec; empty name must fall through
        return `[Function ${value.name || 'anonymous'}]`
      }
      if (typeof value === 'symbol') return String(value)
      if (value && typeof value === 'object') {
        // oxlint-disable-next-line typescript/no-unsafe-argument -- JSON.stringify replacer receives unknown value per spec
        if (seen.has(value)) return '[Circular]'
        // oxlint-disable-next-line typescript/no-unsafe-argument -- JSON.stringify replacer receives unknown value per spec
        seen.add(value)
      }
      // oxlint-disable-next-line typescript/no-unsafe-return -- JSON.stringify replacer receives unknown value per spec
      return value
    })
    return _normalizeInlineText(json ?? 'null')
  } catch {
    // oxlint-disable-next-line typescript/no-base-to-string -- fallback for non-JSON-serializable objects, best effort
    return _normalizeInlineText(String(data))
  }
}

function _normalizeInlineText(value: string): string {
  return value.replaceAll(/\r?\n/g, '\\n')
}

function getColoredType(
  data: { type?: 'mutation' | 'query' | 'subscription' },
  colored: boolean,
) {
  if (!data.type) return colored ? colors.bold(colors.green('?')) : '?'
  if (data.type === 'mutation')
    return colored ? colors.bold(colors.blue('M')) : 'M'
  if (data.type === 'query')
    return colored ? colors.bold(colors.green('Q')) : 'Q'
  return colored ? colors.bold(colors.yellow('S')) : 'S'
}
